import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Plus, Save, Target, Trash2 } from 'lucide-react';
import { useAuth, useData } from '../App';
import { Button, Card, Input, Select } from '../components/UI';
import { BarChart, SERIES_COLORS } from '../components/BarChart';
import { PlanningArticle, PlanningReport, PlanningRow, PlanningSettings, PlanningVatBase, UserRole } from '../types';
import { api } from '../services/api';
import { formatMoney, orgDisplayName, periodLabel, todayISO } from '../utils/format';

type PeriodKind = 'month' | 'quarter' | 'year' | 'ytd';

const periodRange = (kind: PeriodKind, year: number, month: number, quarter: number): [string, string] => {
    const pad = (n: number) => String(n).padStart(2, '0');
    const last = (y: number, m: number) => new Date(y, m, 0).getDate();
    if (kind === 'month') return [`${year}-${pad(month)}-01`, `${year}-${pad(month)}-${last(year, month)}`];
    if (kind === 'quarter') { const m1 = quarter * 3 - 2, m3 = quarter * 3; return [`${year}-${pad(m1)}-01`, `${year}-${pad(m3)}-${last(year, m3)}`]; }
    if (kind === 'ytd') return [`${year}-01-01`, year === new Date().getFullYear() ? todayISO() : `${year}-12-31`];
    return [`${year}-01-01`, `${year}-12-31`];
};

// Настройки статей (с даты): проценты — ровно 100%, привязка категорий операций к статьям, переносы с прошлого года
const SettingsEditor: React.FC<{ settings: PlanningSettings; onSaved: () => void }> = ({ settings, onSaved }) => {
    const { state, showNotification } = useData();
    const last = settings.versions[settings.versions.length - 1];
    const [validFrom, setValidFrom] = useState(todayISO().slice(0, 8) + '01');
    const [articles, setArticles] = useState<PlanningArticle[]>(last.articles);
    const [mapping, setMapping] = useState<Record<string, string | null>>(last.mapping);
    const [carryYear, setCarryYear] = useState(new Date().getFullYear());
    const [carry, setCarry] = useState<Record<string, number>>({});
    const [autoCarry, setAutoCarry] = useState<Record<string, number>>({});
    useEffect(() => { setCarry(settings.carryovers[String(carryYear)] || {}); }, [settings, carryYear]);
    useEffect(() => { api.autoCarryovers(carryYear).then(setAutoCarry).catch(() => setAutoCarry({})); }, [settings, carryYear]);
    const carrySaved = Object.keys(settings.carryovers[String(carryYear)] || {}).length > 0;
    const total = articles.reduce((s, a) => s + Number(a.percent || 0), 0);
    const ok = Math.abs(total - 100) < 1e-9;
    const setArticle = (i: number, patch: Partial<PlanningArticle>) => setArticles(list => list.map((a, j) => j === i ? { ...a, ...patch } : a));

    const save = async () => {
        try { await api.savePlanningVersion({ validFrom, articles, mapping }); showNotification(`Проценты сохранены с ${validFrom}`, 'success'); onSaved(); }
        catch (e: any) { showNotification(e.message || 'Не сохранено', 'error'); }
    };
    const [useCarry, setUseCarry] = useState(settings.useCarryover);
    useEffect(() => setUseCarry(settings.useCarryover), [settings.useCarryover]);
    const toggleCarry = async (v: boolean) => {
        setUseCarry(v);
        try { await api.savePlanningOptions({ useCarryover: v }); onSaved(); }
        catch (e: any) { setUseCarry(!v); showNotification(e.message || 'Не сохранено', 'error'); }
    };
    const [priorToPartner, setPriorToPartner] = useState(settings.priorYearTaxesToPartner);
    useEffect(() => setPriorToPartner(settings.priorYearTaxesToPartner), [settings.priorYearTaxesToPartner]);
    const togglePrior = async (v: boolean) => {
        setPriorToPartner(v);
        try { await api.savePlanningOptions({ priorYearTaxesToPartner: v }); onSaved(); }
        catch (e: any) { setPriorToPartner(!v); showNotification(e.message || 'Не сохранено', 'error'); }
    };
    const [vatBase, setVatBase] = useState(settings.vatBase);
    useEffect(() => setVatBase(settings.vatBase), [settings.vatBase]);
    const changeVatBase = async (v: PlanningVatBase) => {
        const prev = vatBase;
        setVatBase(v);
        try { await api.savePlanningOptions({ vatBase: v }); onSaved(); }
        catch (e: any) { setVatBase(prev); showNotification(e.message || 'Не сохранено', 'error'); }
    };
    const saveCarry = async () => {
        try { await api.saveCarryovers(carryYear, carry); showNotification('Переносы сохранены', 'success'); onSaved(); }
        catch (e: any) { showNotification(e.message || 'Не сохранено', 'error'); }
    };

    return (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <Card title="Статьи распределения">
                <div className="space-y-2 text-sm">
                    <p className="text-xs text-slate-500">Версии: {settings.versions.map(v => v.validFrom === '2000-01-01' ? 'исходная' : `с ${v.validFrom}`).join(', ')}. Новые проценты действуют с выбранной даты, прошлое не пересчитывается.</p>
                    {articles.map((a, i) => (
                        <div key={i} className="flex flex-wrap items-center gap-2">
                            <input className="w-32 rounded border border-slate-300 px-2 py-1" value={a.name} onChange={e => setArticle(i, { name: e.target.value })} />
                            <input className="w-20 rounded border border-slate-300 px-2 py-1 text-right" type="number" step="0.01" value={a.percent} onChange={e => setArticle(i, { percent: Number(e.target.value) })} />
                            <span className="text-slate-500">%</span>
                            <select className="rounded border border-slate-300 px-2 py-1 text-xs" value={a.type} onChange={e => setArticle(i, { type: e.target.value as PlanningArticle['type'] })}>
                                <option value="category">категории операций</option><option value="partner">партнёр</option>
                            </select>
                            {a.type === 'partner' && (
                                <select className="rounded border border-slate-300 px-2 py-1 text-xs" value={a.orgId || ''} onChange={e => setArticle(i, { orgId: e.target.value || null })}>
                                    <option value="">ИП партнёра…</option>
                                    {state.organizations.map(o => <option key={o.id} value={o.id}>{orgDisplayName(o)}</option>)}
                                </select>
                            )}
                            <button onClick={() => setArticles(list => list.filter((_, j) => j !== i))} className="text-slate-400 hover:text-red-600"><Trash2 size={14} /></button>
                        </div>
                    ))}
                    <button onClick={() => setArticles(list => [...list, { key: `A_${Date.now()}`, name: 'Новая статья', percent: 0, type: 'category' }])} className="text-xs text-blue-600 flex items-center gap-1"><Plus size={12} /> Статья</button>
                    <div className={`text-sm font-bold ${ok ? 'text-emerald-700' : 'text-red-600'}`}>Сумма: {total}% {ok ? '' : '— должно быть ровно 100%'}</div>

                    <div className="pt-2 border-t">
                        <div className="text-xs font-bold text-slate-600 mb-1">Категория операции → статья</div>
                        {Object.entries(settings.categories).map(([cat, label]) => (
                            <div key={cat} className="flex items-center justify-between gap-2 py-0.5 text-xs">
                                <span>{label}{settings.pendingDecision.includes(cat) && <span className="text-amber-600" title="Открытый вопрос: «Налог» или «ЗП»"> · уточнить</span>}</span>
                                <select className="rounded border border-slate-300 px-1 py-0.5" value={mapping[cat] ?? ''} onChange={e => setMapping(m => ({ ...m, [cat]: e.target.value || null }))}>
                                    <option value="">не распределяется</option>
                                    {cat === 'WITHDRAWAL' && <option value="@partner">статья партнёра своего ИП</option>}
                                    {articles.map(a => <option key={a.key} value={a.key}>{a.name}</option>)}
                                </select>
                            </div>
                        ))}
                    </div>
                    <div className="flex items-end gap-2 pt-2 border-t">
                        <Input label="Действует с" type="date" value={validFrom} onChange={e => setValidFrom(e.target.value)} />
                        <Button onClick={save} disabled={!ok}><Save className="mr-1 h-4 w-4" /> Сохранить</Button>
                    </div>
                </div>
            </Card>
            <Card title="НДС в базе распределения">
                <div className="space-y-2 text-sm">
                    {(Object.keys(settings.vatBaseModes) as PlanningVatBase[]).map(m => (
                        <label key={m} className="flex items-center gap-2 font-medium cursor-pointer">
                            <input type="radio" name="vatBase" checked={vatBase === m} onChange={() => changeVatBase(m)} />
                            {settings.vatBaseModes[m]}
                        </label>
                    ))}
                    <p className="text-xs text-slate-500">«Как в таблице» — как лист «ПриходыЗП»: из всех приходов (безнал + нал) вычитается 5% безнала
                        плательщика НДС минус уплаченный НДС. «Фактически начисленный» — НДС по ставке каждой операции (5/105).
                        В отчёте видны база и доли в обоих вариантах.</p>
                </div>
            </Card>
            <Card title="Налоги прошлых лет">
                <div className="space-y-2 text-sm">
                    <label className="flex items-center gap-2 font-medium cursor-pointer">
                        <input type="checkbox" checked={priorToPartner} onChange={e => togglePrior(e.target.checked)} />
                        Налоги ИП за прошлые годы, уплаченные в текущем, — на статью партнёра
                    </label>
                    <p className="text-xs text-slate-500">УСН за 4 квартал, 1% и фиксированные взносы прошлого года, погашенные в этом году, идут
                        не в статью «Налог», а в статью партнёра этого ИП: резерв на них партнёр забрал себе в прошлом году.
                        В деньгах это по-прежнему уплата налога (пополнение ЕНС); в блоке «Как в таблице» — строка «Бонус».
                        Если у налога стоит отметка из переноса (в таблице записан или не записан «Бонусом»), действует она.</p>
                </div>
            </Card>
            <Card title="Перенос на начало года («с прошлого года»)">
                <div className="space-y-2 text-sm">
                    <label className="flex items-center gap-2 font-medium cursor-pointer">
                        <input type="checkbox" checked={useCarry} onChange={e => toggleCarry(e.target.checked)} />
                        Учитывать перенос с прошлого года
                    </label>
                    <p className="text-xs text-slate-500">Пока выключено — колонки «Перенос» нет и остаток без него. Суммы переноса ниже хранятся и не удаляются.</p>
                    <Input label="Год" type="number" value={carryYear} onChange={e => setCarryYear(Number(e.target.value))} />
                    {articles.map(a => (
                        <div key={a.key} className="flex items-center justify-between gap-2">
                            <span>{a.name}</span>
                            <input className="w-40 rounded border border-slate-300 px-2 py-1 text-right" type="number" step="0.01" value={carry[a.key] ?? ''}
                                   onChange={e => setCarry(c => ({ ...c, [a.key]: Number(e.target.value) }))} />
                        </div>
                    ))}
                    {!carrySaved && Object.keys(autoCarry).length > 0 && (
                        <p className="text-xs text-emerald-700">За {carryYear} суммы не вводили — перенос берётся автоматически из остатков статей партнёров
                            на конец {carryYear - 1} года: {articles.filter(a => autoCarry[a.key] != null).map(a => `${a.name} ${formatMoney(autoCarry[a.key])}`).join(', ')}.</p>
                    )}
                    {carrySaved && <p className="text-xs text-slate-500">За {carryYear} сохранены свои суммы — автоматический перенос не используется. Очистите все поля и сохраните, чтобы вернуть его.</p>}
                    <div className="flex flex-wrap gap-2">
                        <Button variant="outline" onClick={saveCarry}><Save className="mr-1 h-4 w-4" /> Сохранить переносы</Button>
                        {Object.keys(autoCarry).length > 0 && (
                            <Button variant="outline" type="button" onClick={() => setCarry({ ...autoCarry })}>Подставить остатки {carryYear - 1}</Button>
                        )}
                    </div>
                    <p className="text-xs text-slate-500">Если перенос включён, он учитывается в остатке, когда период начинается 1 января (год, с начала года, 1 квартал, январь).</p>
                </div>
            </Card>
        </div>
    );
};

const otherMode = (m: PlanningVatBase): PlanningVatBase => (m === 'TABLE' ? 'ACTUAL' : 'TABLE');

const TableValue: React.FC<{ v?: number | null }> = ({ v }) =>
    v == null ? null : <div className="text-[10px] font-normal text-slate-400">табл. {formatMoney(v)}</div>;

// Выписка по партнёру: план (база × %), перенос с прошлого года, бонусы по источникам, остаток; рядом — таблица
const PartnerStatements: React.FC<{ report: PlanningReport }> = ({ report }) => {
    const rows: PlanningRow[] = report.articles.filter(r => r.bonuses);
    const line = (label: string, app: number | null, table?: number | null, cls = '') => (
        <tr className={cls}>
            <td className="px-3 py-1">{label}</td>
            <td className="px-3 py-1 text-right font-mono">{app == null ? '' : formatMoney(app)}</td>
            <td className="px-3 py-1 text-right font-mono text-slate-400">{table == null ? '' : formatMoney(table)}</td>
        </tr>);
    return (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
            {rows.map(r => {
                const b = r.bonuses!;
                const t = r.table || {};
                return (
                    <Card key={r.key} className="bg-white" title={`Выписка: ${r.name}`}>
                        <table className="w-full text-xs">
                            <thead className="text-slate-500"><tr><th></th><th className="text-right px-3">Приложение</th><th className="text-right px-3">Таблица</th></tr></thead>
                            <tbody>
                                {line(`План (база × ${r.percent}%)`, r.plan, t.plan)}
                                {line(report.carrySource === 'auto' ? 'Перенос с прошлого года (авто)' : 'Перенос с прошлого года', r.carry, t.carry)}
                                {line('Бонусы, всего', -b.total, t.fact != null ? -t.fact : null, 'font-medium')}
                                {line('  из таблицы', b.table ? -b.table : 0)}
                                {line('  налоги прошлого года', b.taxes ? -b.taxes : 0)}
                                {line('  корректировка при переходе', b.transition ? -b.transition : 0)}
                                {b.other !== 0 && line('  введены в приложении', -b.other)}
                                {line('Остаток', r.remaining, t.left, 'font-bold border-t')}
                                {b.transition !== 0 && line('Остаток до корректировки', r.remainingBeforeTransition ?? null, t.left, 'text-slate-500')}
                            </tbody>
                        </table>
                        {!report.useCarryover && <p className="text-[11px] text-amber-700 mt-1">Перенос выключен в настройках — остаток без него.</p>}
                    </Card>
                );
            })}
        </div>
    );
};

export const Planning: React.FC = () => {
    const { revision } = useData();
    const { user } = useAuth();
    const now = new Date();
    const [kind, setKind] = useState<PeriodKind>('ytd');
    const [year, setYear] = useState(now.getFullYear());
    const [month, setMonth] = useState(now.getMonth() + 1);
    const [quarter, setQuarter] = useState(Math.floor(now.getMonth() / 3) + 1);
    const [report, setReport] = useState<PlanningReport | null>(null);
    const [settings, setSettings] = useState<PlanningSettings | null>(null);
    const [showSettings, setShowSettings] = useState(false);
    const [from, to] = periodRange(kind, year, month, quarter);

    const loadSeq = useRef(0);
    const load = useCallback(() => {
        const seq = ++loadSeq.current;
        const fresh = () => seq === loadSeq.current;
        api.getPlanningReport(from, to).then(r => { if (fresh()) setReport(r); }).catch(() => { if (fresh()) setReport(null); });
        api.getPlanningSettings().then(setSettings).catch(() => setSettings(null));
    }, [from, to]);
    useEffect(load, [load, revision]);

    const chart = useMemo(() => (report?.monthly || []).map(m => ({ label: periodLabel(m.month), values: { plan: m.plan, fact: m.fact } })), [report]);

    return (
        <div className="space-y-4">
            <div className="flex flex-wrap justify-between items-center gap-2">
                <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2"><Target /> Планирование</h1>
                {user?.role === UserRole.ADMIN && <Button variant="outline" onClick={() => setShowSettings(v => !v)}>{showSettings ? 'Скрыть настройки' : 'Настройки статей'}</Button>}
            </div>

            <Card className="bg-white">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 items-end">
                    <Select label="Период" value={kind} onChange={e => setKind(e.target.value as PeriodKind)} options={[
                        { value: 'month', label: 'Месяц' }, { value: 'quarter', label: 'Квартал' }, { value: 'year', label: 'Год' }, { value: 'ytd', label: 'С начала года' }]} />
                    <Input label="Год" type="number" value={year} onChange={e => setYear(Number(e.target.value))} />
                    {kind === 'month' && <Select label="Месяц" value={String(month)} onChange={e => setMonth(Number(e.target.value))} options={Array.from({ length: 12 }, (_, i) => ({ value: String(i + 1), label: periodLabel(`${year}-${String(i + 1).padStart(2, '0')}`) }))} />}
                    {kind === 'quarter' && <Select label="Квартал" value={String(quarter)} onChange={e => setQuarter(Number(e.target.value))} options={[1, 2, 3, 4].map(q => ({ value: String(q), label: `${q} квартал` }))} />}
                </div>
            </Card>

            {showSettings && settings && <SettingsEditor settings={settings} onSaved={load} />}

            {report && (
                <>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-2 text-sm">
                        <Card className="bg-white"><div className="text-xs text-slate-500">Приходы за период</div><div className="text-lg font-bold">{formatMoney(report.incomeGross)}</div>
                            {report.cashIn > 0 && <div className="text-[11px] text-slate-500">в т. ч. наличные «не доход» («нал» таблицы) {formatMoney(report.cashIn)}</div>}</Card>
                        <Card className="bg-white"><div className="text-xs text-slate-500">НДС: {report.vatBaseModes[report.vatBase]}</div><div className="text-lg font-bold">−{formatMoney(report.vat)}</div>
                            {(Object.keys(report.bases) as PlanningVatBase[]).filter(m => m !== report.vatBase).map(m => (
                                <div key={m} className="text-[11px] text-slate-500">{report.vatBaseModes[m]}: −{formatMoney(report.bases[m].vat)}</div>))}</Card>
                        <Card className="bg-white"><div className="text-xs text-slate-500">База распределения</div><div className="text-lg font-bold text-blue-700">{formatMoney(report.base)}</div>
                            {(Object.keys(report.bases) as PlanningVatBase[]).filter(m => m !== report.vatBase).map(m => (
                                <div key={m} className="text-[11px] text-slate-500">{report.vatBaseModes[m]}: {formatMoney(report.bases[m].base)}</div>))}
                            {report.tableBase != null && <div className="text-[11px] text-slate-500">в таблице: {formatMoney(report.tableBase)} (разница {formatMoney(report.base - report.tableBase)})</div>}</Card>
                    </div>

                    <div className="bg-white rounded-lg border border-slate-200 overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead className="bg-slate-50 text-slate-600 text-xs"><tr>
                                <th className="text-left px-3 py-2">Статья</th><th className="text-right px-3 py-2">%</th><th className="text-right px-3 py-2">План</th>
                                <th className="text-right px-3 py-2">Факт</th>{report.useCarryover && <th className="text-right px-3 py-2">Перенос</th>}<th className="text-right px-3 py-2">Остаток</th>
                                <th className="px-3 py-2 w-40">Использовано</th><th className="text-right px-3 py-2" title="Неоплаченные счета × % статьи">Ожидается</th>
                            </tr></thead>
                            <tbody className="divide-y divide-slate-100">
                                {report.articles.map(r => {
                                    const avail = r.plan + Math.max(0, r.carry);
                                    const used = avail > 0 ? Math.min(100, (r.fact / avail) * 100) : (r.fact > 0 ? 100 : 0);
                                    // Перерасход пока не подсвечиваем (ТЗ v4 §7): нейтральный остаток, с минусом если отрицательный
                                    return (
                                        <tr key={r.key}>
                                            <td className="px-3 py-2 font-medium">{r.name}{r.type === 'partner' && <span className="text-[10px] text-slate-400"> · партнёр</span>}</td>
                                            <td className="px-3 py-2 text-right">{r.percent}%</td>
                                            <td className="px-3 py-2 text-right font-mono" title={`${report.vatBaseModes[otherMode(report.vatBase)]}: ${formatMoney(r.planOther)}`}>{formatMoney(r.plan)}<TableValue v={r.table?.plan} /></td>
                                            <td className="px-3 py-2 text-right font-mono">{formatMoney(r.fact)}<TableValue v={r.table?.fact} /></td>
                                            {report.useCarryover && <td className="px-3 py-2 text-right font-mono text-slate-500">{r.carry ? formatMoney(r.carry) : '—'}</td>}
                                            <td className="px-3 py-2 text-right font-mono font-bold text-slate-900 whitespace-nowrap">{formatMoney(r.remaining)}<TableValue v={r.table?.left} /></td>
                                            <td className="px-3 py-2">
                                                <div className="h-2 bg-slate-100 rounded" title={`${Math.round(used)}%`}>
                                                    <div className="h-2 rounded bg-blue-600" style={{ width: `${used}%` }} />
                                                </div>
                                            </td>
                                            <td className="px-3 py-2 text-right font-mono text-slate-500">{formatMoney(r.expected)}</td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                            <tfoot className="bg-slate-50 font-bold text-xs"><tr>
                                <td className="px-3 py-2">Итого</td><td></td>
                                <td className="px-3 py-2 text-right font-mono">{formatMoney(report.totals.plan)}</td>
                                <td className="px-3 py-2 text-right font-mono">{formatMoney(report.totals.fact)}</td>
                                {report.useCarryover && <td className="px-3 py-2 text-right font-mono">{formatMoney(report.totals.carry)}</td>}
                                <td className="px-3 py-2 text-right font-mono">{formatMoney(report.totals.remaining)}</td><td></td>
                                <td className="px-3 py-2 text-right font-mono">{formatMoney(report.totals.expected)}</td>
                            </tr></tfoot>
                        </table>
                    </div>

                    {report.articles.some(r => r.table) && <p className="text-[11px] text-slate-500 -mt-2">Серым под суммой — значение таблицы «Мани» (лист «ПриходыЗП», до корректировок при переходе).</p>}

                    {report.articles.some(r => r.bonuses) && <PartnerStatements report={report} />}

                    {report.unmapped.length > 0 && (
                        <Card className="bg-amber-50 border-amber-200">
                            <div className="text-xs space-y-0.5">
                                <div className="font-bold text-amber-800">Не распределено по статьям</div>
                                {report.unmapped.map(u => (
                                    <div key={u.category} className="flex justify-between"><span>{u.name}{u.pendingDecision && ' — открытый вопрос: «Налог» или «ЗП»?'}</span><span className="font-mono">{formatMoney(u.amount)}</span></div>
                                ))}
                            </div>
                        </Card>
                    )}

                    <Card className="bg-white">
                        <BarChart title="План и факт по месяцам" data={chart} height={200}
                                  series={[{ key: 'plan', name: 'План', color: SERIES_COLORS[0] }, { key: 'fact', name: 'Факт', color: SERIES_COLORS[1] }]} />
                    </Card>
                </>
            )}
        </div>
    );
};
